import { useState, type FormEvent } from 'react';
import { useGameStore } from '@campus/core';
import { getTenantSlug } from '../../lib/tenant.js';
import { API_BASE } from '../../lib/apiBase.js';
import { LogoMark } from '../ds/index.js';

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: 'var(--space-3) var(--space-4)',
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--color-border-strong)',
  background: 'var(--color-bg-raised)',
  color: 'var(--color-text-primary)',
  fontSize: 'var(--text-sm)',
  fontFamily: 'var(--font-body)',
  outline: 'none',
  boxSizing: 'border-box',
  transition: 'border-color var(--transition-base)',
};

const btnStyle: React.CSSProperties = {
  width: '100%',
  padding: 'var(--space-3)',
  borderRadius: 'var(--radius-md)',
  border: 'none',
  background: 'var(--color-primary)',
  color: '#fff',
  fontSize: 'var(--text-base)',
  fontFamily: 'var(--font-body)',
  fontWeight: 'var(--font-bold)' as unknown as number,
  cursor: 'pointer',
  transition: 'opacity var(--transition-base)',
};

/**
 * GuestPage — Formulaire d'entree guest via lien d'invitation.
 *
 * URL : /guest?invite={id}
 * Affiche un champ prenom uniquement.
 * POST /auth/guest → JWT ephemere → redirect vers le campus.
 * ZERO ecriture identite en base — RGPD compliant.
 */
export function GuestPage({ inviteId }: { inviteId: string }) {
  const [displayName, setDisplayName] = useState('');
  const [error, setError]             = useState<string | null>(null);
  const [loading, setLoading]         = useState(false);

  const setJwt       = useGameStore((s) => s.setJwt);
  const setAuthState = useGameStore((s) => s.setAuthState);
  const setProfile   = useGameStore((s) => s.setProfile);
  const setTargetZoneSlug  = useGameStore((s) => s.setTargetZoneSlug);
  const setTargetSpawnName = useGameStore((s) => s.setTargetSpawnName);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const name = displayName.trim();
    if (!name || name.length > 30) {
      setError('Ton prénom doit faire entre 1 et 30 caractères.');
      return;
    }
    setError(null);
    setLoading(true);

    try {
      const tenantSlug = getTenantSlug();
      const res = await fetch(`${API_BASE}/auth/guest`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Tenant-ID': tenantSlug,
        },
        body: JSON.stringify({ inviteLinkId: inviteId, displayName: name }),
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({})) as { error?: string };
        const code = body.error ?? `Erreur ${res.status}`;
        const messages: Record<string, string> = {
          INVALID_INVITE:       'Ce lien d\'invitation n\'existe pas.',
          INVITE_REVOKED:       'Ce lien a été révoqué.',
          INVITE_EXPIRED:       'Ce lien a expiré.',
          INVITE_EXHAUSTED:     'Ce lien a atteint le nombre maximum d\'utilisations.',
          INVALID_DISPLAY_NAME: 'Prénom invalide (30 caractères maximum).',
        };
        throw new Error(messages[code] ?? code);
      }

      const data = await res.json() as { token: string };
      // Reset la zone cible d'une eventuelle session precedente dans le meme
      // onglet — voir meme fix dans LoginPage.tsx.
      setTargetZoneSlug(''); // carte d'arrivée du campus
      setTargetSpawnName(null);
      setJwt(data.token);
      setProfile(name, 0);
      setAuthState('authenticated');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur de connexion');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100vw',
        height: '100dvh',
        background: 'var(--color-bg-base)',
        fontFamily: 'var(--font-body)',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 380,
          background: 'var(--color-bg-surface)',
          border: '1px solid var(--color-border-default)',
          borderRadius: 'var(--radius-xl)',
          padding: 'var(--space-10) var(--space-8)',
          boxShadow: 'var(--shadow-lg)',
        }}
      >
        <div style={{ textAlign: 'center', marginBottom: 'var(--space-8)' }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 'var(--space-4)' }}>
            <LogoMark size={100} />
          </div>
          <h1 style={{
            margin: 0,
            color: 'var(--color-text-primary)',
            fontSize: 'var(--text-xl)',
            fontFamily: 'var(--font-display)',
            fontWeight: 'var(--font-bold)' as unknown as number,
            letterSpacing: 'var(--tracking-tight)',
          }}>
            Placio
          </h1>
          <p style={{
            margin: '6px 0 0',
            color: 'var(--color-text-muted)',
            fontSize: 'var(--text-sm)',
          }}>
            Entre avec ton lien d'invitation
          </p>
        </div>

        <form onSubmit={(e) => { void handleSubmit(e); }} noValidate>
          <div>
            <label style={{
              display: 'block',
              color: 'var(--color-text-secondary)',
              fontSize: 'var(--text-xs)',
              fontFamily: 'var(--font-body)',
              fontWeight: 'var(--font-medium)' as unknown as number,
              marginBottom: 'var(--space-2)',
              textTransform: 'uppercase',
              letterSpacing: 'var(--tracking-wide)',
            }}>
              Ton prénom
            </label>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Ex. : Camille"
              maxLength={30}
              required
              autoFocus
              style={inputStyle}
              onFocus={(e) => { (e.target as HTMLInputElement).style.borderColor = 'var(--color-primary)'; }}
              onBlur={(e) => { (e.target as HTMLInputElement).style.borderColor = 'var(--color-border-strong)'; }}
            />
          </div>

          {error && (
            <div
              style={{
                marginTop: 'var(--space-3)',
                padding: 'var(--space-3) var(--space-4)',
                borderRadius: 'var(--radius-md)',
                background: 'var(--color-danger-muted)',
                border: '1px solid rgba(240, 112, 112, 0.3)',
                color: 'var(--color-danger)',
                fontSize: 'var(--text-sm)',
              }}
            >
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            style={{ ...btnStyle, marginTop: 'var(--space-6)', opacity: loading ? 0.6 : 1 }}
          >
            {loading ? 'Connexion…' : 'Rejoindre'}
          </button>
        </form>
      </div>
    </div>
  );
}
